import { useState } from "react";

import { Button } from "common/components/ui/Button";
import { CountSpinner } from "common/components/ui/CountSpinner";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import WorkspaceImage from "common/components/WorkspaceImage";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import {
  getLifetimeLicenseSource,
  getLifetimeLicenseSourceCount,
} from "common/helpers/workspaceLicenseCounts";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import type { Workspace } from "common/types";

import LifetimeLicenseSummary from "../../LifetimeLicenseSummary";

interface ManageSeatsDrawerProps {
  workspace: Workspace;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export default function ManageSeatsDrawer({
  workspace,
  isOpen,
  setIsOpen,
}: ManageSeatsDrawerProps) {
  const { id, lifetime_license_count, name, icon_url } = workspace;

  const [seatCount, setSeatCount] = useState(lifetime_license_count);

  const { ltdPromotion, appSumoPromotion, lifetimeUnassignedLicenseCount } =
    usePromotions();
  const hasLifetimePromotion = !!ltdPromotion || !!appSumoPromotion;

  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();

  const { outreachLicensedAccounts } = useLicensedAccounts(id);
  const { workspace: workspaceDetails } = useWorkspaceDetails(id);

  async function onSubmit() {
    const source = getLifetimeLicenseSource(
      ltdPromotion,
      appSumoPromotion,
      lifetime_license_count,
      seatCount,
      workspaceDetails?.licenses,
    );
    if (seatCount !== lifetime_license_count && source) {
      const count = getLifetimeLicenseSourceCount(
        workspaceDetails?.licenses ?? [],
        source,
        lifetime_license_count,
        seatCount,
      );
      await setLifetimeLicenseCount({
        workspaceId: id,
        count,
        source,
        accountsToCancel: [],
      });
    }
    setIsOpen(false);
  }

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent>
        <DrawerHeader title="Manage seats" />

        <DrawerBody>
          <LifetimeLicenseSummary />

          <div className="border rounded-3xl bg-purple-50/30 border-purple-50 my-4">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr className="text-purple-500 text-xs text-left">
                  <th className="px-3 py-2.5">Name</th>
                  <th className="px-3 py-2.5">Accounts / Seats</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td className="p-3 border-l rounded-l-3xl border-y bg-white border-black-200">
                    <div className="gap-x-2 flex items-center">
                      <WorkspaceImage id={id} imageSrc={icon_url} />
                      <span className="text-sm-bold">{name}</span>
                    </div>
                  </td>
                  <td className="p-3 border-r rounded-r-3xl border-y bg-white border-black-200">
                    <span className="text-button-xs ml-8">
                      {outreachLicensedAccounts.length}{" "}
                      <span className="text-black-400">
                        / {lifetime_license_count}
                      </span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {hasLifetimePromotion && (
            <CountSpinner
              variant="lg"
              value={seatCount}
              setValue={setSeatCount}
              maxValue={lifetime_license_count + lifetimeUnassignedLicenseCount}
            />
          )}
        </DrawerBody>

        <DrawerFooter className="justify-end">
          <DrawerClose asChild disabled={isSettingLifetimeLicenseCount}>
            <Button variant="secondary-black">Cancel</Button>
          </DrawerClose>

          <Button onClick={onSubmit} isLoading={isSettingLifetimeLicenseCount}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
